import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Lightning } from "@phosphor-icons/react";

const empty = { title: "", body: "", shortcut: "" };

export default function CompanyTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/company/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ title: t.title, body: t.body, shortcut: t.shortcut || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/company/templates/${editing.id}`, form);
        toast.success("Template atualizado");
      } else {
        await api.post("/company/templates", form);
        toast.success("Template criado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir "${t.title}"?`)) return;
    try {
      await api.delete(`/company/templates/${t.id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Automação</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Templates</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Respostas rápidas com variáveis <span className="font-mono text-[#0F4C3A]">{"{nome}"}</span> e <span className="font-mono text-[#0F4C3A]">{"{empresa}"}</span>.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="new-template-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
              <Plus size={18} weight="bold" className="mr-2" /> Novo template
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
            <DialogHeader><DialogTitle>{editing ? "Editar template" : "Novo template"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Título (visível internamente)</Label>
                <Input data-testid="template-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" placeholder="Ex: Saudação inicial" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Atalho (opcional)</Label>
                <Input data-testid="template-shortcut-input" value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value })} className="rounded-none mt-1 font-mono" placeholder="/oi" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Mensagem</Label>
                <textarea data-testid="template-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full mt-1 border border-[#E5E5E0] p-3 text-sm min-h-[120px] font-mono" placeholder="Olá {nome}, aqui é da {empresa}. Como posso ajudar?" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancelar</Button>
              <Button data-testid="template-save-btn" onClick={save} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="templates-grid">
        {items.length === 0 && (
          <div className="col-span-2 p-10 text-center text-sm text-[#5C5C59] bg-white border border-[#E5E5E0] shadow-brutal">
            Nenhum template ainda. Clique em "Novo template" para começar.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-[#E5E5E0] p-5 shadow-brutal">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold tracking-tight truncate">{t.title}</h3>
                {t.shortcut && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono font-bold text-[#0F4C3A] bg-[#EDF3F0] px-2 py-0.5">
                    <Lightning size={10} weight="bold" /> {t.shortcut}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="p-2 hover:bg-[#F0F0EE] transition-colors"><PencilSimple size={16} /></button>
                <button onClick={() => remove(t)} data-testid={`delete-template-${t.id}`} className="p-2 hover:bg-[#F0F0EE] text-[#DC2626] transition-colors"><Trash size={16} /></button>
              </div>
            </div>
            <div className="text-sm text-[#1A1A1A] whitespace-pre-wrap font-mono bg-[#F7F7F5] p-3 border border-[#E5E5E0]">
              {t.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
